import { isKeywordMapPlaceholder } from "./keywordMapTableData";

export type KeywordMapJobStatus = "pending" | "running" | "finished" | "failed";

export type KeywordMapJob = {
  id: string;
  kind: string;
  status: KeywordMapJobStatus;
  error?: string;
};

export type KeywordMapKeyword = {
  id: string;
  url: string;
  top_keyword: string;
  keyword: string;
  clicks: number;
  impressions: number;
  position: number;
  pre_clicks: number | null;
  pre_impressions: number | null;
  pre_position: number | null;
  search_volume: number | null;
};

export type KeywordMapTag = {
  id: string;
  title: string;
  type?: string;
  position?: number;
};

/** One URL and the queries stored under it. The API tree is flattened here. */
export type KeywordMapUrlRow = {
  id: string;
  url: string;
  slug: string;
  keyword: string;
  prio_url: string;
  top_keyword: string;
  customer_journey: string;
  tags: string;
  clicks: number;
  impressions: number;
  position: number;
  pre_clicks: number | null;
  pre_impressions: number | null;
  pre_position: number | null;
  Tags: KeywordMapTag[];
  keywords: KeywordMapKeyword[];
};

export type KeywordMapCheck = {
  status?: string;
  updated_at?: string;
  configuration?: { property_site_url?: string };
};

export type KeywordMapPage = {
  rows: KeywordMapUrlRow[];
  page: Record<string, unknown>;
  check: KeywordMapCheck | null;
  job: KeywordMapJob | null;
  stale: boolean;
};

type RawKeyword = {
  id?: string | number;
  url?: string;
  top_keyword?: string;
  keyword?: string;
  clicks?: number;
  impressions?: number;
  position?: number;
  pre_clicks?: number | null;
  pre_impressions?: number | null;
  pre_position?: number | null;
  search_volume?: number | null;
};

type RawUrl = {
  id?: string | number;
  url?: string;
  slug?: string;
  keyword?: string;
  prio_url?: string;
  top_keyword?: string;
  customer_journey?: string;
  tags?: string;
  clicks?: number;
  impressions?: number;
  position?: number;
  pre_clicks?: number | null;
  pre_impressions?: number | null;
  pre_position?: number | null;
  Tags?: KeywordMapTag[] | null;
  Childrens?: RawKeyword[] | null;
};

function text(value: unknown): string {
  return value == null ? "" : String(value);
}

function numberOrNull(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function numberOrZero(value: unknown): number {
  return numberOrNull(value) ?? 0;
}

export function keywordMapJobStatus(value: unknown): KeywordMapJobStatus | null {
  switch (value) {
    case "pending":
    case "running":
    case "finished":
    case "failed":
      return value;
    default:
      return null;
  }
}

/** Pending and running jobs are still working. Finished and failed are terminal. */
export function keywordMapJobIsActive(status: KeywordMapJobStatus | null | undefined): boolean {
  switch (status) {
    case "pending":
    case "running":
      return true;
    case "finished":
    case "failed":
    case null:
    case undefined:
      return false;
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}

/** A keyword check stops polling once the worker has finished or failed. */
export function keywordMapCheckSettled(status: string | null | undefined): boolean {
  return status === "finished" || status === "failed";
}

function keywordFromRaw(raw: RawKeyword): KeywordMapKeyword {
  return {
    id: text(raw.id),
    url: text(raw.url),
    top_keyword: text(raw.top_keyword),
    keyword: text(raw.keyword),
    clicks: numberOrZero(raw.clicks),
    impressions: numberOrZero(raw.impressions),
    position: numberOrZero(raw.position),
    pre_clicks: numberOrNull(raw.pre_clicks),
    pre_impressions: numberOrNull(raw.pre_impressions),
    pre_position: numberOrNull(raw.pre_position),
    search_volume: numberOrNull(raw.search_volume),
  };
}

/** Drops the Tabulator placeholder child and keeps real queries on the URL. */
export function keywordMapUrlRows(data: unknown): KeywordMapUrlRow[] {
  const rows = Array.isArray(data) ? (data as RawUrl[]) : [];
  return rows.map((row) => {
    const children = Array.isArray(row.Childrens) ? row.Childrens : [];
    return {
      id: text(row.id),
      url: text(row.url),
      slug: text(row.slug),
      keyword: text(row.keyword),
      prio_url: text(row.prio_url),
      top_keyword: text(row.top_keyword),
      customer_journey: text(row.customer_journey),
      tags: text(row.tags),
      clicks: numberOrZero(row.clicks),
      impressions: numberOrZero(row.impressions),
      position: numberOrZero(row.position),
      pre_clicks: numberOrNull(row.pre_clicks),
      pre_impressions: numberOrNull(row.pre_impressions),
      pre_position: numberOrNull(row.pre_position),
      Tags: Array.isArray(row.Tags) ? row.Tags : [],
      keywords: children
        .filter((child) => !isKeywordMapPlaceholder(child))
        .map((child) => keywordFromRaw(child)),
    };
  });
}

export function normalizeKeywordMapPage(body: unknown): KeywordMapPage {
  const source = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  const jobRaw = source.job && typeof source.job === "object" ? (source.job as Record<string, unknown>) : null;
  const status = keywordMapJobStatus(jobRaw?.status);
  return {
    rows: keywordMapUrlRows(source.data),
    page: source.page && typeof source.page === "object" ? (source.page as Record<string, unknown>) : {},
    check: source.keywordMapCheck && typeof source.keywordMapCheck === "object"
      ? (source.keywordMapCheck as KeywordMapCheck)
      : null,
    job: jobRaw && status
      ? {
          id: text(jobRaw.id),
          kind: text(jobRaw.kind),
          status,
          error: text(jobRaw.error),
        }
      : null,
    stale: source.stale === true,
  };
}
